import React, { useState } from "react";
import { Formik } from "formik";
import { CreateChapterDto } from "../../../interfaces/chapters.interface";
import { Question } from "../../../interfaces/questions.interface";
import ChapterService from "../../../service/ChapterService";
import { ChapterFormProps } from "./types";
import { validationSchema } from "./validation";
import { initialValues } from "./initialValues";
import { StepOne } from "./steps/StepOne";
import { StepTwo } from "./steps/StepTwo";
import { StepThree } from "./steps/StepThree";
import { StepProgress } from "./components/StepProgress";
import { validateStep } from "./validation/stepValidation";
import axios from "axios";

const steps = [
  { title: "Informations", component: StepOne },
  { title: "Questions", component: StepTwo },
  { title: "Récapitulatif", component: StepThree }
];

export const ChapterForm: React.FC<ChapterFormProps> = ({ mode, initialChapter, onSubmitSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = async (values: CreateChapterDto) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const chapterService = new ChapterService();
      const validQuestions = values.questions?.filter(q => q !== null) || [];
      const dataToSubmit = { ...values, questions: validQuestions };

      if (mode === 'edit' && initialChapter) {
        await chapterService.updateChapter(initialChapter._id, dataToSubmit);
      } else {
        await chapterService.createChapter(dataToSubmit);
      }
      onSubmitSuccess();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(mode === 'edit' ? "Erreur lors de la modification du chapitre" : "Erreur lors de la création du chapitre");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async (values: CreateChapterDto, setTouched: (touched: any) => void) => {
    if (currentStep === 0) {
      setTouched({ title: true, category: true, description: true });
    } else if (currentStep === 1) {
      setTouched({ questions: true });
    }

    const validQuestions = values.questions?.filter(q => q !== null) || [];
    const valuesWithValidQuestions = { ...values, questions: validQuestions };

    const { isValid, errors } = await validateStep(valuesWithValidQuestions, currentStep);
    setStepErrors(errors);

    if (isValid) {
      setCurrentStep(currentStep + 1);
      setStepErrors({});
      setTouched({});
    } else {
      setError("Veuillez corriger les erreurs avant de continuer");
    }
  };

  const handlePrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentStep(currentStep - 1);
    setStepErrors({});
    setError(null);
  };

  const formInitialValues = mode === 'edit' && initialChapter
    ? {
        title: initialChapter.title,
        category: initialChapter.category,
        description: initialChapter.description,
        questions: initialChapter.questions?.filter(q => q !== null) || []
      }
    : initialValues;

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
      enableReinitialize={true}
    >
      {({ values, handleChange, setFieldValue, errors, touched, setTouched }) => {
        const handleQuestionAdded = (question: Omit<Question, '_id' | 'chapter'>) => {
          const currentQuestions = values.questions?.filter(q => q !== null) || [];
          setFieldValue('questions', [...currentQuestions, question]);
        };

        const handleQuestionRemoved = (index: number) => {
          const currentQuestions = values.questions?.filter(q => q !== null) || [];
          const newQuestions = [...currentQuestions];
          newQuestions.splice(index, 1);
          setFieldValue('questions', newQuestions);
        };

        const handleQuestionEdited = (index: number, editedQuestion: Omit<Question, '_id' | 'chapter'>) => {
          const currentQuestions = values.questions?.filter(q => q !== null) || [];
          const newQuestions = [...currentQuestions];
          newQuestions[index] = editedQuestion;
          setFieldValue('questions', newQuestions);
        };

        const currentValues = {
          ...values,
          questions: values.questions?.filter(q => q !== null) || []
        };

        const baseProps = {
          values: currentValues,
          onChange: handleChange,
          errors: { ...errors, ...stepErrors },
          touched,
        };

        const renderCurrentStep = () => {
          switch (currentStep) {
            case 0:
              return <StepOne {...baseProps} />;
            case 1:
              return (
                <StepTwo
                  {...baseProps}
                  onQuestionAdded={handleQuestionAdded}
                  onQuestionEdited={handleQuestionEdited}
                  onRemoveQuestion={handleQuestionRemoved}
                />
              );
            case 2:
              return <StepThree {...baseProps} />;
            default:
              return null;
          }
        };

        return (
          <div className="flex flex-col h-full">
            <div className="flex-shrink-0 mb-6">
              <StepProgress steps={steps} currentStep={currentStep} />
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              {renderCurrentStep()}

              {error && (
                <div className="text-red-600 text-sm mt-4 p-2 bg-red-50 rounded-md">
                  {error}
                </div>
              )}
            </div>

            <div className="flex-shrink-0 flex justify-between pt-4 mt-6 border-t">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="inline-flex items-center px-4 py-2 bg-white border border-primary text-primary hover:bg-primary-focus rounded-md focus:outline-none focus:ring-0 font-sans font-semibold"
                >
                  <i className="pi pi-arrow-left mr-2" />
                  Précédent
                </button>
              )}
              <div className="ml-auto">
                {currentStep < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => handleNext(values, setTouched)}
                    className="inline-flex items-center px-4 py-2 bg-primary border-none text-white hover:bg-primary-focus rounded-md focus:outline-none focus:ring-0 font-sans font-semibold"
                  >
                    Suivant
                    <i className="pi pi-arrow-right ml-2" />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => handleFormSubmit(values)}
                    className="inline-flex items-center px-4 py-2 bg-primary text-white hover:bg-primary-focus rounded-md focus:outline-none focus:ring-0 font-sans font-semibold"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="pi pi-spinner pi-spin mr-2" />
                        {mode === 'edit' ? 'Modification en cours...' : 'Création en cours...'}
                      </>
                    ) : (
                      <>
                        <i className="pi pi-check mr-2" />
                        {mode === 'edit' ? 'Modifier le chapitre' : 'Créer le chapitre'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export * from "./types";
